import { FC, useEffect, useState } from 'react';
import { Button, Input, Modal, Typography } from 'antd';
import { useNotificationContext } from '@/features/notifications';
import { useDeleteWorkspace, useGetWorkspaces } from '@/shared/api';
import { useRouterPrivateContext } from '@/shared/router';
import { Col, Row } from '@/shared/ui';

type Props = {
  open: boolean;
  onCancel: () => void;
};

const { Title } = Typography;

export const DeleteWorkspaceModal: FC<Props> = ({ open, onCancel }) => {
  const { selectedWorkspace, setSelectedWorkspace } = useRouterPrivateContext();
  const { showNotification } = useNotificationContext();

  const [name, setName] = useState('');

  const { data: workspacesData, refetch: refetchWorkspaces } = useGetWorkspaces(
    {},
    {
      select: ({ data }) => data,
    },
  );
  const { mutate: deleteWorkspace } = useDeleteWorkspace();

  const handleWorkspaceDelete = () => {
    deleteWorkspace(
      {
        pathParams: { id: selectedWorkspace?.id || '' },
      },
      {
        onSuccess() {
          const deletedWorkspace = { ...selectedWorkspace };
          const updatedWorkspaces = workspacesData?.filter(
            ({ id }) => id !== deletedWorkspace.id,
          );
          setSelectedWorkspace(updatedWorkspaces?.[0]);
          refetchWorkspaces();
          showNotification({
            type: 'success',
            message: 'Workspace deleted',
            description: `Workspace ${deletedWorkspace.title} has been deleted successfully`,
          });
          onCancel();
        },
        onError(error) {
          if (error.status === 403 && error.message) {
            showNotification({
              type: 'error',
              message: 'Delete workspace error',
              description: error.message,
            });
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!open) {
      setName('');
    }
  }, [open]);

  return (
    <Modal
      data-testid="delete-workspace-modal"
      title={
        <Col
          alignItems="flex-start"
          style={{ margin: '10px 0', width: '100%' }}
        >
          <Title level={4} style={{ lineHeight: 1, marginTop: 15 }}>
            Delete workspace
          </Title>
        </Col>
      }
      width={350}
      open={open}
      footer={null}
      style={{ maxWidth: '90%' }}
      onCancel={onCancel}
    >
      <div style={{ marginTop: 20 }}>
        Are you sure you want to delete{' '}
        <span
          style={{
            fontWeight: 'bold',
            borderRadius: 6,
            textDecoration: 'underline',
          }}
        >
          {selectedWorkspace?.title}
        </span>{' '}
        workspace?
        <br />
        <br />
        This action is <strong>irreversible.</strong>
        <br />
        <br />
      </div>
      <div>Enter the workspace name to confirm.</div>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        style={{ marginTop: 10 }}
      />

      <Row>
        <Button
          onClick={onCancel}
          style={{ width: '40%', height: 40, marginTop: 30 }}
        >
          Cancel
        </Button>
        <Button
          danger
          type="primary"
          disabled={name !== selectedWorkspace?.title}
          onClick={handleWorkspaceDelete}
          style={{ width: '60%', height: 40, marginTop: 30 }}
        >
          Delete
        </Button>
      </Row>
    </Modal>
  );
};
